<template>
  <div class="devices">
    <WelcomePopup v-if="getShowWelcome" @start-tour="startHomePageTour" />
    <DevicePopup
      @close="closeDetailModal"
      :currentDevice="currentDevice"
      :isOpen="modalIsOpen"
    ></DevicePopup>
    <contact-us-form
      title="Can’t Find the Device You Need?"
      :formId="suggestDevice"
      :show="showSuggestDevice"
      @close="showSuggestDevice = false"
    />

    <div class="devices__body --highlighted">
      <div class="devices__filter_panel">
        <div class="filter-list" data-v-step="0">
          <Select
            class="!w-[160px]"
            v-model="deviceTypeFilter"
            :default-text="$t('filter.type.title')"
            :options="filterTypes.DEVICE_OPTIONS"
            multiple
            searchable
          >
          </Select>
          <Select
            class="!w-[160px]"
            v-model="osFilter"
            :default-text="$t('filter.os.title')"
            :options="filterTypes.OS_OPTIONS"
            multiple
            searchable
          >
          </Select>
          <Select
            class="!w-[160px]"
            v-model="statusFilter"
            :default-text="$t('filter.status.title')"
            :options="filterTypes.STATUS_OPTIONS"
            multiple
            searchable
          >
          </Select>
          <SelectList
            class="!w-[160px]"
            :default-text="$t('filter.other.title')"
            :selections="[
              brandFilter,
              resolutionFilter,
              sizeFilter,
              osvFilter,
              nfcFilter,
            ]"
            :options="[
              filterTypes.BRAND_OPTIONS,
              filterTypes.RESOLUTION_OPTIONS,
              filterTypes.SIZE_OPTIONS,
              filterTypes.OSV_OPTIONS,
              filterTypes.NFC_OPTIONS,
            ]"
          >
            <Select
              class="!w-[160px]"
              v-model="brandFilter"
              :default-text="$t('filter.brand.title')"
              :options="filterTypes.BRAND_OPTIONS"
              multiple
              searchable
            />
            <Select
              class="!w-[160px]"
              v-model="resolutionFilter"
              :default-text="$t('filter.resolution.title')"
              :options="filterTypes.RESOLUTION_OPTIONS"
              multiple
              searchable
            />
            <Select
              class="!w-[160px]"
              v-model="sizeFilter"
              :default-text="$t('filter.size.title')"
              :options="filterTypes.SIZE_OPTIONS"
              multiple
              searchable
            />
            <Select
              class="!w-[160px]"
              v-model="osvFilter"
              :default-text="$t('filter.version.title')"
              :options="filterTypes.OSV_OPTIONS"
              multiple
              searchable
            />
            <Select
              class="!w-[160px]"
              v-model="nfcFilter"
              :default-text="$t('filter.nfc.title')"
              :options="filterTypes.NFC_OPTIONS"
              multiple
              searchable
            />
          </SelectList>
          <div
            v-if="clearFilterControl"
            class="clear-filters"
            @click="clearFilter"
          >
            <span
              class="text-primary-base text-xs font-extrabold dark:text-secondary-300"
            >
              {{ $t("filter.general.clear") }}
            </span>
          </div>
        </div>
      </div>
      <div class="flex flex-row justify-between items-center">
        <p class="font-bold text-xs dark:text-primary-white">
          {{ $t("device.list.title") }}
          <span class="text-secondary-300 font-medium text-xs">
            {{ deviceNumber }}</span
          >
        </p>
        <div class="flex flex-row items-center gap-3.5">
          <Button
            class="refresh-button"
            :disabled="refreshLoading"
            @click="refreshDevices"
          >
            <svg
              :class="{ rotate_svg: refreshLoading }"
              xmlns="http://www.w3.org/2000/svg"
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
            >
              <path
                d="M1.99951 12C1.99951 6.47997 6.43951 1.99997 11.9995 1.99997C18.6695 1.99997 21.9995 7.55997 21.9995 7.55997M21.9995 7.55997V2.55997M21.9995 7.55997H17.5595"
                stroke="#2B5DE5"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
              <path
                opacity="0.4"
                d="M21.89 12C21.89 17.52 17.41 22 11.89 22C6.37 22 3 16.44 3 16.44M3 16.44H7.52M3 16.44V21.44"
                stroke="#2B5DE5"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
              <path
                v-if="!refreshLoading && refreshStatus === 'success'"
                xmlns="http://www.w3.org/2000/svg"
                d="M9 12.5L11 15L15 10"
                stroke="#22C55E"
                stroke-width="1.5"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
              <path
                v-else-if="!refreshLoading && refreshStatus === 'failed'"
                xmlns="http://www.w3.org/2000/svg"
                d="M10.4749 14.5084L14.5084 10.4757C14.7799 10.2042 14.7799 9.76402 14.5084 9.49254C14.2368 9.22106 13.7966 9.22106 13.5251 9.49254L9.49164 13.5253C9.22012 13.7968 9.22012 14.2369 9.49164 14.5084C9.76317 14.7799 10.2034 14.7799 10.4749 14.5084Z"
                fill="#FF4747"
              />
            </svg>
          </Button>
          <div class="device__filter" data-v-step="1">
            <div
              @click="toggleView(false)"
              :class="{
                'device__filter__item--active': isGridView,
              }"
              class="device__filter__item w-12 rounded-l-lg"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 19 19"
                fill="none"
              >
                <path
                  d="M17.0488 6.82008V3.79008C17.0488 2.59758 16.5688 2.11758 15.3763 2.11758H12.3463C11.1538 2.11758 10.6738 2.59758 10.6738 3.79008V6.82008C10.6738 8.01258 11.1538 8.49258 12.3463 8.49258H15.3763C16.5688 8.49258 17.0488 8.01258 17.0488 6.82008Z"
                  stroke="#292D32"
                  stroke-width="1.5"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
                <path
                  d="M8.42383 7.00758V3.60258C8.42383 2.54508 7.94383 2.11758 6.75133 2.11758H3.72133C2.52883 2.11758 2.04883 2.54508 2.04883 3.60258V7.00008C2.04883 8.06508 2.52883 8.48508 3.72133 8.48508H6.75133C7.94383 8.49258 8.42383 8.06508 8.42383 7.00758Z"
                  stroke="#292D32"
                  stroke-width="1.5"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
                <path
                  d="M17.0488 15.4451V12.4151C17.0488 11.2226 16.5688 10.7426 15.3763 10.7426H12.3463C11.1538 10.7426 10.6738 11.2226 10.6738 12.4151V15.4451C10.6738 16.6376 11.1538 17.1176 12.3463 17.1176H15.3763C16.5688 17.1176 17.0488 16.6376 17.0488 15.4451Z"
                  stroke="#292D32"
                  stroke-width="1.5"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
                <path
                  d="M8.42383 15.4451V12.4151C8.42383 11.2226 7.94383 10.7426 6.75133 10.7426H3.72133C2.52883 10.7426 2.04883 11.2226 2.04883 12.4151V15.4451C2.04883 16.6376 2.52883 17.1176 3.72133 17.1176H6.75133C7.94383 17.1176 8.42383 16.6376 8.42383 15.4451Z"
                  stroke="#292D32"
                  stroke-width="1.5"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
              </svg>
            </div>
            <div
              @click="toggleView(true)"
              :class="{
                'device__filter__item--active': !isGridView,
              }"
              class="device__filter__item w-12 rounded-r-lg"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 19 19"
                fill="none"
              >
                <path
                  d="M17.1992 6.82008V3.79008C17.1992 2.59758 16.7192 2.11758 15.5267 2.11758H12.4967C11.3042 2.11758 10.8242 2.59758 10.8242 3.79008V6.82008C10.8242 8.01258 11.3042 8.49258 12.4967 8.49258H15.5267C16.7192 8.49258 17.1992 8.01258 17.1992 6.82008Z"
                  stroke="#292D32"
                  stroke-width="1.5"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
                <path
                  d="M8.57422 7.00758V3.60258C8.57422 2.54508 8.09422 2.11758 6.90172 2.11758H3.87172C2.67922 2.11758 2.19922 2.54508 2.19922 3.60258V7.00008C2.19922 8.06508 2.67922 8.48508 3.87172 8.48508H6.90172C8.09422 8.49258 8.57422 8.06508 8.57422 7.00758Z"
                  stroke="#292D32"
                  stroke-width="1.5"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
                <path
                  d="M8.57422 15.4451V12.4151C8.57422 11.2226 8.09422 10.7426 6.90172 10.7426H3.87172C2.67922 10.7426 2.19922 11.2226 2.19922 12.4151V15.4451C2.19922 16.6376 2.67922 17.1176 3.87172 17.1176H6.90172C8.09422 17.1176 8.57422 16.6376 8.57422 15.4451Z"
                  stroke="#292D32"
                  stroke-width="1.5"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
                <path
                  d="M11.9492 12.2426H16.4492"
                  stroke="#292D32"
                  stroke-width="1.5"
                  stroke-linecap="round"
                />
                <path
                  d="M11.9492 15.2426H16.4492"
                  stroke="#292D32"
                  stroke-width="1.5"
                  stroke-linecap="round"
                />
              </svg>
            </div>
          </div>
        </div>
      </div>
      <div v-if="loading" class="loading">
        <div class="lds-ripple">
          <div></div>
          <div></div>
        </div>
      </div>
      <div v-if="devices.length === 0 && !loading" class="no-device">
        <img
          v-if="theme"
          src="@/assets/images/no-match-dark.svg"
          alt="no device"
        />
        <img v-else src="@/assets/images/no-match.svg" alt="no device" />
        <p v-if="isSearched" class="text-center">
          <b class="!font-semibold">{{ $t("input.no_result.sorry") }}</b>
          {{ $t("input.no_result.subtitle") }}<br />
          {{ $t("input.no_result.devicelist") }}
        </p>
        <p v-else>
          <b class="!font-semibold">{{ $t("filter.no_result.sorry") }}</b>
          {{ $t("filter.no_result.title") }}
        </p>
        <Button
          v-if="isSearched"
          class="primary large"
          @click="showSuggestDevice = true"
        >
          {{ $t("suggest") }}
        </Button>
      </div>
      <div class="flex flex-col gap-4">
        <div
          v-if="devices.length"
          :class="{ 'card-grid': !isListView, 'card-list': isListView }"
        >
          <div v-if="isListView" class="w-[1100px] list-view__header">
            <div
              @click="sortList('deviceName')"
              class="list-view__header__items translate-x-[104px]"
            >
              <span class="list-view__header__items__text">
                {{ $t("device.spec.name") }}
              </span>
              <SortingArrow :sortingTitles="sortingTitles.deviceName" />
            </div>
            <div></div>
            <div
              id="status"
              @click="sortList('status')"
              class="list-view__header__items translate-x-[10px]"
              :class="{ '!translate-x-[39px]': isLangTr }"
            >
              <span class="list-view__header__items__text">
                {{ $t("device.list.status") }}
              </span>
              <SortingArrow :sortingTitles="sortingTitles.status" />
            </div>
            <div
              @click="sortList('osVersion')"
              class="list-view__header__items translate-x-[29px]"
              :class="{ '!translate-x-[58px]': isLangTr }"
            >
              <span class="list-view__header__items__text">
                {{ $t("device.spec.version") }}
              </span>
              <SortingArrow :sortingTitles="sortingTitles.osVersion" />
            </div>
            <div
              @click="sortList('resolution')"
              class="list-view__header__items translate-x-[3px]"
              :class="{ '!translate-x-[27px]': isLangTr }"
            >
              <span class="list-view__header__items__text">
                {{ $t("device.spec.resolution") }}
              </span>
              <div class="list-view__header__arrows">
                <img
                  v-if="
                    sortingTitles['resolution'] === 'asc' ||
                    sortingTitles['resolution'] === 'default'
                  "
                  src="@/assets/images/arrow-up.svg"
                  alt=""
                />
                <img
                  v-if="
                    sortingTitles['resolution'] === 'desc' ||
                    sortingTitles['resolution'] === 'default'
                  "
                  src="@/assets/images/arrow-down.svg"
                  alt=""
                />
              </div>
            </div>
            <div
              @click="sortList('screenSize')"
              class="list-view__header__items translate-x-[-6px]"
              :class="{ '!translate-x-[20px]': isLangTr }"
            >
              <span class="list-view__header__items__text">
                {{ $t("device.spec.size") }}
              </span>
              <SortingArrow :sortingTitles="sortingTitles.screenSize" />
            </div>
            <div
              class="list-view__header__items translate-x-[-26px]"
              :class="{ '!translate-x-[-2px]': isLangTr }"
            >
              <span class="list-view__header__items__text">
                {{ $t("device.list.detail") }}
              </span>
            </div>
            <div
              class="list-view__header__items translate-x-[-5px]"
              :class="{ '!translate-x-[0px]': isLangTr }"
            >
              <span class="list-view__header__items__text">
                {{ $t("device.list.action") }}
              </span>
            </div>
          </div>
          <DeviceItem
            :class="{ 'v-step-2': device.isTrialDevice }"
            @open-detail="openDetailModal"
            :key="device.id"
            :device="device"
            :isTrialDevice="device.isTrialDevice"
            :isOpen="isListView"
            v-for="device in devicesToShow"
          />
        </div>
      </div>
    </div>
    <v-tour
      name="homePageTour"
      :steps="steps"
      :options="{ highlight: true }"
    ></v-tour>
  </div>
</template>

<script>
import _ from "lodash";
import { mapActions, mapGetters } from "vuex";
import DeviceItem from "@/components/DeviceItem";
import DevicePopup from "@/components/DevicePopup";
import { DEVICE_FILTERS, FORM_ID_LIST, DEVICE_TOUR, STATUS } from "@/constants";
import WelcomePopup from "@/components/WelcomePopup";
import Select from "@/components/Select";
import SelectList from "@/components/Select/SelectList";
import Button from "@/components/Button";
import SortingArrow from "@/components/SortingArrow";
const ContactUsForm = () => import("@/components/ContactUsForm");

export default {
  name: "Devices",
  components: {
    DevicePopup,
    DeviceItem,
    WelcomePopup,
    Select,
    Button,
    SortingArrow,
    ContactUsForm,
    SelectList,
  },
  props: {
    status: String,
    deviceName: String,
    osVersion: String,
    resolution: String,
    screenSize: String,
  },
  data() {
    return {
      devices: [],
      filters: [],
      osFilter: [],
      deviceTypeFilter: [],
      statusFilter: [],
      osvFilter: [],
      brandFilter: [],
      resolutionFilter: [],
      sizeFilter: [],
      nfcFilter: [],
      filterValue: [],
      isGridView: true,
      q: "",
      currentDevice: null,
      modalIsOpen: false,
      loading: false,
      refreshLoading: false,
      refreshStatus: "",
      fromRefresh: false,
      isListView: false,
      sortedDevices: [],
      sortAsc: false,
      sortingTitles: {
        status: "default",
        deviceName: "default",
        osVersion: "default",
        resolution: "default",
        screenSize: "default",
      },
      steps: DEVICE_TOUR,
      suggestDevice: FORM_ID_LIST.SUGGEST_DEVICE,
      showSuggestDevice: false,
      isLangTr: false,
    };
  },
  sockets: {
    sessionEnd() {
      this.destroySession();
    },
  },
  created() {
    const savedIsListView = localStorage.getItem("isListView");
    if (savedIsListView) {
      this.isListView = JSON.parse(savedIsListView);
    }
    this.theme = this.getTheme === "dark";
  },
  methods: {
    ...mapActions([
      "getDevices",
      "triggerApplicationModal",
      "getApps",
      "emitMixPanelEvent",
      "changeConnectionStatus",
      "destroySession",
    ]),
    exitSession() {
      this.$socket.emit("sessionEnd", this.session.room);
    },
    getFiltersFromStorage() {
      this.filters = JSON.parse(sessionStorage?.getItem("filters") ?? "[]");
      const prevQ = JSON.parse(sessionStorage.getItem("value"));
      this.$nextTick(() => {
        this.$root.$emit("update-header-q", prevQ);
      });
      this.filters.forEach((filter) => {
        switch (filter.filter) {
          case "status":
            this.statusFilter = filter.value;
            break;
          case "deviceOs":
            this.osFilter = filter.value;
            break;
          case "deviceType":
            this.deviceTypeFilter = filter.value;
            break;
          case "deviceName":
            this.filterValue = filter.value;
            break;
          case "deviceBrand":
            this.filterValue = filter.value;
            break;
          case "deviceOsv":
            this.osvFilter = filter.value;
            break;
          case "brand":
            this.brandFilter = filter.value;
            break;
          case "resolution":
            this.resolutionFilter = filter.value;
            break;
          case "screenSize":
            this.sizeFilter = filter.value;
            break;
          case "nfcSupport":
            this.nfcFilter = filter.value;
            break;
        }
      });
    },
    defaultSort() {
      let groups = _.groupBy(this.devices.slice(), (o) => {
        return o.status;
      });
      let sorteredGroups = _.orderBy(groups, ["createdAt"], ["desc"]);
      this.sortedDevices = _.flatten(sorteredGroups);
      this.sorting();
    },
    sortList(title) {
      Object.keys(this.sortingTitles).forEach((key) => {
        if (key !== title) {
          this.sortingTitles[key] = "default";
        }
      });
      if (this.sortingTitles[title] === "desc") {
        this.sortAsc = false;
        this.sortingTitles[title] = "default";
        this.defaultSort();
        return;
      }
      if (this.sortingTitles[title] === "default") {
        this.sortAsc = true;
        this.sortingTitles[title] = "asc";
      } else {
        this.sortAsc = false;
        this.sortingTitles[title] = "desc";
      }
      this.sortedDevices = _.orderBy(
        this.devices,
        (t) => {
          if (
            title === "osVersion" ||
            title === "screenSize" ||
            title === "resolution"
          ) {
            return parseFloat(t[title].split(".").join(""));
          } else {
            return t[title];
          }
        },
        [this.sortAsc ? "asc" : "desc"]
      );
    },
    toggleView(isListView) {
      this.isListView = isListView;
      this.isGridView = !isListView;
      localStorage.setItem("isListView", JSON.stringify(this.isListView));
    },
    clearFilter() {
      this.deviceTypeFilter = [];
      this.osFilter = [];
      this.statusFilter = [];
      this.osvFilter = [];
      this.brandFilter = [];
      this.resolutionFilter = [];
      this.sizeFilter = [];
      this.nfcFilter = [];
      this.q = "";
      // this.filters = [];
      this.defaultSort();
    },
    checkFilterIsApplied(filter) {
      return _.some(this.filters, { filter });
    },
    openDetailModal(device) {
      this.modalIsOpen = true;
      this.currentDevice = device;
    },
    removeFilter(filter) {
      this.filters = this.filters.filter((f) => {
        return f.filter !== filter;
      });
    },
    closeDetailModal() {
      this.modalIsOpen = false;
      this.currentDevice = null;
    },
    filterOs() {
      this.emitMixPanelEvent({
        event: "deviceFilter",
        data: {
          filterDeviceOS: this.osFilter,
        },
      });
      this.filters.push({
        filter: "deviceOs",
        value: this.osFilter,
        text: "Device OS",
        textVal: this.osFilter,
      });
    },
    filterOsv() {
      this.emitMixPanelEvent({
        event: "deviceFilter",
        data: {
          filterDeviceOSVersion: this.osvFilter,
        },
      });
      this.filters.push({
        filter: "deviceOsv",
        value: this.osvFilter,
        text: "Device OS Version",
        textVal: this.osvFilter,
      });
    },
    filterBrand() {
      this.emitMixPanelEvent({
        event: "deviceFilter",
        data: {
          filterDeviceBrand: this.brandFilter,
        },
      });
      this.filters.push({
        filter: "brand",
        value: this.brandFilter,
        text: "Device Brand",
        textVal: this.brandFilter,
      });
    },
    filterResolution() {
      this.emitMixPanelEvent({
        event: "deviceFilter",
        data: {
          filterDeviceBrand: this.resolutionFilter,
        },
      });
      this.filters.push({
        filter: "resolution",
        value: this.resolutionFilter,
        text: "Device Resolution",
        textVal: this.resolutionFilter,
      });
    },
    filterSize() {
      this.emitMixPanelEvent({
        event: "deviceFilter",
        data: {
          filterDeviceScreenSize: this.sizeFilter,
        },
      });
      this.filters.push({
        filter: "screenSize",
        value: this.sizeFilter,
        text: "Device Screen Size",
        textVal: this.sizeFilter,
      });
    },
    filterNFC() {
      this.emitMixPanelEvent({
        event: "deviceFilter",
        data: {
          filterDeviceNFC: this.nfcFilter,
        },
      });
      this.filters.push({
        filter: "nfcSupport",
        value: this.nfcFilter,
        text: "Device NFC",
        textVal: this.nfcFilter,
      });
    },
    filterDeviceType() {
      this.emitMixPanelEvent({
        event: "deviceFilter",
        data: {
          filterDeviceType: this.deviceTypeFilter,
        },
      });
      this.filters.push({
        filter: "deviceType",
        value: this.deviceTypeFilter,
        text: "Device Type",
        textVal: this.deviceTypeFilter,
      });
    },
    filterStatus() {
      this.emitMixPanelEvent({
        event: "deviceFilter",
        data: {
          filterDeviceStatus: this.statusFilter,
        },
      });
      this.filters.push({
        filter: "status",
        value: this.statusFilter,
        text: "Status",
        textVal: this.statusFilter,
      });
    },
    searchText(value) {
      this.filterValue = value || "";
      this.emitMixPanelEvent({
        event: "filterSearch",
        data: {
          filterSearch: this.filterValue,
        },
      });
      this.filters.push({
        filter: "deviceName",
        value: this.filterValue,
        text: "Search Text",
        textVal: this.filterValue,
      });
      this.filters.push({
        filter: "deviceBrand",
        value: this.filterValue,
        text: "Search Brand Text",
        textVal: this.filterValue,
      });
    },
    startHomePageTour() {
      this.$tours["homePageTour"].start();
    },
    addToText(current, newText, isAdd) {
      let temp = current;
      if (isAdd) {
        temp = temp.length ? newText : `${temp}, ${newText}`;
      }
      return temp;
    },
    refreshDevices() {
      this.refreshLoading = true;
      this.getDevices()
        .then((devices) => {
          this.fromRefresh = true;
          this.devices = devices;
          this.refreshStatus = "success";
          this.refreshLoading = false;
          setTimeout(() => {
            this.refreshStatus = "";
          }, 2000);
        })
        .catch(() => {
          this.refreshStatus = "failed";
          this.refreshLoading = false;
          setTimeout(() => {
            this.refreshStatus = "";
          }, 2000);
        })
        .finally(() => {
          this.getFiltersFromStorage();
        });
    },
    sorting() {
      let title = Object.keys(this.sortingTitles).find(
        (key) => this.sortingTitles[key] !== "default"
      );
      this.sortedDevices = _.orderBy(
        this.devices,
        (t) => {
          if (
            title === "osVersion" ||
            title === "screenSize" ||
            title === "resolution"
          ) {
            return parseFloat(t[title].split(".").join(""));
          } else {
            return t[title];
          }
        },
        [this.sortAsc ? "asc" : "desc"]
      );
    },
  },
  computed: {
    ...mapGetters(["deviceList", "getShowWelcome", "session", "getTheme"]),
    filterTypes() {
      let types = DEVICE_FILTERS;
      Object.keys(types).forEach((key) => {
        types[key].map((item) => {
          item.text = this.$t(item.text);
          return item;
        });
      });
      return types;
    },
    deviceNumber() {
      return this.filters.length
        ? `(${this.devices?.length ?? 0} / ${
            this.deviceList?.length ?? 0
          } ${this.$t("device.list.counts")})`
        : `(${this.devices?.length ?? 0} ${this.$t("device.list.counts")})`;
    },
    devicesToShow() {
      if (this.isListView) {
        return this.sortedDevices;
      } else {
        let groups = _.groupBy(this.devices.slice(), (o) => {
          return o.status;
        });
        let sorteredGroups = _.orderBy(groups, ["createdAt"], ["desc"]);
        return _.flatten(sorteredGroups);
      }
    },
    clearFilterControl() {
      return (
        this.filters.length > 0 &&
        this.filters.some(
          (item) =>
            item.filter !== "deviceName" && item.filter !== "deviceBrand"
        )
      );
    },
    isSearched() {
      return this.$route.query.q !== undefined;
    },
  },
  beforeRouteEnter(to, from, next) {
    if (
      (to.name === "Devices" && from.name === "DeviceLauncherAndroid") ||
      (to.name === "Devices" && from.name === "DeviceLauncherIos")
    ) {
      next((vm) => {
        vm.exitSession();
      });
    } else {
      next();
    }
  },
  watch: {
    "$route.query.q": function (value) {
      this.removeFilter("deviceName");
      this.removeFilter("deviceBrand");
      if (value !== undefined && value !== "") {
        this.searchText(value);
      }
    },
    osFilter: {
      handler(value) {
        this.removeFilter("deviceOs");
        if (value.length) {
          this.filterOs();
        }
      },
    },
    osvFilter: {
      handler(value) {
        this.removeFilter("deviceOsv");
        if (value.length) {
          this.filterOsv();
        }
      },
    },
    statusFilter: {
      handler(value) {
        this.removeFilter("status");
        if (value.length) {
          this.filterStatus();
        }
      },
    },
    deviceTypeFilter: {
      handler(value) {
        this.removeFilter("deviceType");
        if (value.length) {
          this.filterDeviceType();
        }
      },
    },
    brandFilter: {
      handler(value) {
        this.removeFilter("brand");
        if (value.length) {
          this.filterBrand();
        }
      },
    },
    resolutionFilter: {
      handler(value) {
        this.removeFilter("resolution");
        if (value.length) {
          this.filterResolution();
        }
      },
    },
    sizeFilter: {
      handler(value) {
        this.removeFilter("screenSize");
        if (value.length) {
          this.filterSize();
        }
      },
    },
    nfcFilter: {
      handler(value) {
        this.removeFilter("nfcSupport");
        if (value.length) {
          this.filterNFC();
        }
      },
    },
    filters: {
      handler(filters) {
        sessionStorage.setItem("filters", JSON.stringify(filters));
        if (filters.length === 0) {
          this.devices = this.deviceList;
        } else {
          let condition = {
            deviceBrand: null,
            deviceOs: null,
            status: null,
            deviceName: "",
            deviceType: null,
            brand: null,
            deviceOsv: null,
            resolution: null,
            screenSize: null,
            nfcSupport: null,
          };
          filters.forEach((filter) => {
            condition[filter.filter] = filter.value;
            if (filter.filter == "deviceName") {
              filter.value = this.$route.query.q || "";
            }
            if (filter.filter == "deviceBrand") {
              filter.value = this.$route.query.q || "";
            }
          });
          this.devices = _.filter(this.deviceList, (device) => {
            return (
              (condition.deviceOs?.includes(device.deviceOs.toLowerCase()) ??
                true) &&
              (condition.brand?.includes(device.deviceBrand) ?? true) &&
              (condition.deviceOsv?.includes(device.osVersion) ?? true) &&
              (condition.resolution?.includes(device.resolution) ?? true) &&
              (condition.status?.includes(device.status) ?? true) &&
              (condition.screenSize?.includes(device.screenSize) ?? true) &&
              (condition.nfcSupport?.includes(device.isNfcSupport) ?? true) &&
              (condition.deviceType?.length == 1
                ? (this.deviceTypeFilter[0] === "tablet") === device.isTablet
                : true) &&
              (device.deviceName
                .turkishToLower()
                .includes(condition.deviceName.turkishToLower()) ||
                device.deviceBrand
                  .turkishToLower()
                  .includes(condition.deviceBrand.turkishToLower()))
            );
          });
        }
      },
    },
    devices: {
      handler: function (newVal) {
        if (newVal) {
          if (!this.fromRefresh) {
            this.defaultSort();
          }
          this.fromRefresh = false;
        }
      },
    },
    isListView: {
      handler: function (newVal) {
        if (!newVal) {
          this.defaultSort();
          this.sortList();
        }
        this.sortAsc = false;
      },
    },
  },
  mounted() {
    this.changeConnectionStatus(STATUS.NOT_CONNECTED);
    this.loading = true;
    this.devices = this.deviceList ?? [];
    this.getDevices()
      .then((devices) => {
        this.devices = devices;
        this.defaultSort();
      })
      .finally(() => {
        this.getFiltersFromStorage();
        this.loading = false;
      });
    this.$parent.$on("clear-filter", (filters) => {
      this.filters = this.filters.filter(
        (filter) => !filters.includes(filter.filter)
      );
    });
    if (localStorage.getItem("pref_lang") === "tr") {
      this.isLangTr = true;
    }
  },
};
</script>

<style>
@import "index.module.css";
</style>
